import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseCopyToClipboardResult {
    copied: boolean;
    copy: (text: string) => Promise<void>;
}

const DEFAULT_RESET_DELAY_MS = 2000;

// Shared "inline label-swap" copy pattern: write to the clipboard, flip
// `copied` to true, then reset it after a couple of seconds so a button can
// show a checkmark/"Copied" state. This is the low-stakes pattern (order/txn
// IDs, JSON responses, code samples) — see dev-portal enterprise redesign
// spec §5.3. High-stakes secret copies (API keys, webhook signing secrets)
// keep the separate toast-confirmed `copyStringToClipboard` (@utils/index).
export function useCopyToClipboard(
    resetDelayMs: number = DEFAULT_RESET_DELAY_MS
): UseCopyToClipboardResult {
    const [copied, setCopied] = useState(false);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => () => clearTimeout(resetTimeoutRef.current), []);

    const copy = useCallback(
        async (text: string) => {
            try {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                clearTimeout(resetTimeoutRef.current);
                resetTimeoutRef.current = setTimeout(() => setCopied(false), resetDelayMs);
            } catch {
                // Clipboard unavailable in insecure contexts — every existing
                // inline-copy call site silently ignores this, so `copied` just
                // stays false rather than surfacing an error.
            }
        },
        [resetDelayMs]
    );

    return { copied, copy };
}
